import * as React from "react";
import Sparkles2FillIcon from "@src/lib/icons/18px/sparkles-2-fill";

import { cn } from "@src/lib/utils/cn";

// The labelled "working on it" status pill, standalone so surfaces that don't
// want the full overlay treatment (e.g. the doc converting view) can show
// just the pill.
export function AgentWorkingPill({
  label = "Building screen",
  className,
}: {
  /** Text before the animated ellipsis. */
  label?: string;
  className?: string;
}): React.ReactElement {
  return (
    <span
      role="status"
      aria-label={`${label}…`}
      className={cn(
        "flex items-center gap-1.5 rounded-full border border-purple-6 bg-purple-1 px-3 py-1 text-3xs-v4 font-medium-v4 text-purple-9 shadow-default",
        className,
      )}
    >
      <Sparkles2FillIcon
        className="size-3.5 animate-pulse"
        aria-hidden="true"
      />
      <span className="animate-pulse">{label}…</span>
    </span>
  );
}

// Wraps any block the agent is actively generating — a screen, a card, a plan —
// and overlays the "working on it" state: the content dims behind a purple
// ring, a soft band scans top→bottom, and a labelled pill names the task.
// Match the wrapper's rounding to the child (pass it via `className`) so the
// ring hugs the content's corners.
export function AgentWorking({
  label = "Building screen",
  active = true,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  /** Text before the animated ellipsis in the status pill. */
  label?: string;
  /** When false, renders children plainly with no overlay. */
  active?: boolean;
}): React.ReactElement {
  return (
    <div className={cn("relative", className)} {...props}>
      <div
        className={cn(
          "transition-[opacity,filter] duration-300",
          active && "opacity-70 blur-[1px]",
        )}
        // The content shouldn't be operable while it's being generated.
        aria-hidden={active || undefined}
        inert={active || undefined}
      >
        {children}
      </div>

      {active && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit] ring-2 ring-purple-6 ring-inset">
          {/* The scan band sweeps top→bottom: a soft, blurred purple glow with a
              faint leading edge so it reads as light passing over the screen. */}
          <div className="agent-scan absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-transparent via-purple-alpha-4 to-transparent blur-[2px]">
            <div className="absolute inset-x-0 bottom-0 h-px bg-purple-alpha-4" />
          </div>
          <div className="absolute inset-x-0 bottom-3 flex justify-center">
            <AgentWorkingPill label={label} />
          </div>
        </div>
      )}
    </div>
  );
}
